import React, { useState, useEffect, useCallback } from 'react';
import * as S from './style';

const CarouselSlider = ({ slides = [], autoPlay = true, interval = 5000 }) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [progress, setProgress] = useState(0);

    const nextSlide = useCallback(() => {
        if (!isTransitioning) {
            setIsTransitioning(true);
            setCurrentSlide((prev) => (prev + 1) % slides.length);
        }
    }, [slides.length, isTransitioning]);

    const goToSlide = useCallback(
        (index) => {
            if (!isTransitioning && index !== currentSlide) {
                setIsTransitioning(true);
                setCurrentSlide(index);
                // 클릭으로 이동할 때 해당 슬라이드의 시작 지점으로 프로그레스 설정
                setProgress((index / slides.length) * 100);
            }
        },
        [currentSlide, isTransitioning, slides.length]
    );

    useEffect(() => {
        if (isTransitioning) {
            const timer = setTimeout(() => {
                setIsTransitioning(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isTransitioning]);

    // 실시간 프로그레스 바 업데이트
    useEffect(() => {
        if (!autoPlay) return;

        const progressInterval = setInterval(() => {
            setProgress((prev) => {
                const nextProgress = prev + 100 / slides.length / (interval / 50); // 50ms마다 업데이트

                // 다음 슬라이드로 넘어갈 시점 체크
                if (nextProgress >= ((currentSlide + 1) / slides.length) * 100) {
                    nextSlide();
                    return ((currentSlide + 1) / slides.length) * 100;
                }

                return nextProgress;
            });
        }, 50);

        return () => clearInterval(progressInterval);
    }, [autoPlay, interval, nextSlide, currentSlide, slides.length]);

    // 슬라이드 변경 시 프로그레스 조정
    useEffect(() => {
        // 현재 슬라이드의 시작 지점으로 프로그레스 설정
        setProgress((currentSlide / slides.length) * 100);
    }, [currentSlide, slides.length]);

    if (!slides || slides.length === 0) {
        return <S.CarouselContainer>슬라이드가 없습니다.</S.CarouselContainer>;
    }

    const currentSlideData = slides[currentSlide];

    return (
        <S.CarouselContainer>
            <S.CarouselWrapper>
                {/* 왼쪽 섹션 - 텍스트와 캐러셀 항목 */}
                <S.CarouselLeftSection>
                    <S.CarouselContent>
                        <S.CarouselTitle>{currentSlideData.title}</S.CarouselTitle>
                        <S.CarouselDescription>{currentSlideData.description}</S.CarouselDescription>

                        {/* 프로그레스 바 */}
                        <S.CarouselProgress>
                            <S.ProgressBar>
                                <S.ProgressFill
                                    style={{
                                        width: `${progress}%`,
                                        transition: 'width 0.05s linear',
                                    }}
                                />
                            </S.ProgressBar>
                        </S.CarouselProgress>

                        {/* 캐러셀 항목 목록 */}
                        <S.CarouselItems>
                            {slides.map((slide, index) => (
                                <S.CarouselItem
                                    key={index}
                                    className={index === currentSlide ? 'active' : ''}
                                    onClick={() => goToSlide(index)}
                                >
                                    <S.ItemIcon>
                                        {slide.icon || (
                                            <svg
                                                width="16"
                                                height="16"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M12 2L2 7L12 12L22 7L12 2Z"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                                <path
                                                    d="M2 17L12 22L22 17"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                                <path
                                                    d="M2 12L12 17L22 12"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        )}
                                    </S.ItemIcon>
                                    <S.ItemContent>
                                        <S.ItemTitle>{slide.summary}</S.ItemTitle>
                                    </S.ItemContent>
                                </S.CarouselItem>
                            ))}
                        </S.CarouselItems>
                    </S.CarouselContent>
                </S.CarouselLeftSection>

                {/* 오른쪽 섹션 - 이미지 */}
                <S.CarouselRightSection>
                    <S.CarouselImageContainer>
                        <S.CarouselImage
                            src={currentSlideData.image}
                            alt={currentSlideData.title || `Slide ${currentSlide + 1}`}
                        />
                    </S.CarouselImageContainer>
                </S.CarouselRightSection>
            </S.CarouselWrapper>
        </S.CarouselContainer>
    );
};

export default CarouselSlider;
